import { wardrobeTemplates } from "../content/wardrobe";
import type { AppData, CosplayCandidate, CosplayPreferences, CosplayProject, ProjectTask, WardrobeItem } from "./types";

const newId = (prefix: string) => `${prefix}-${crypto.randomUUID()}`;

export function createProject(candidate: CosplayCandidate, preferences: CosplayPreferences, today = new Date()): CosplayProject {
  const tasks: ProjectTask[] = [
    { id: newId("task"), stage: "discover", label: "캐릭터·버전 확정", status: "done", dueLabel: "오늘" },
    { id: newId("task"), stage: "prepare", label: "의상 파츠와 실측 확인", status: "doing", dueLabel: "D-35" },
    { id: newId("task"), stage: "prepare", label: "행사·스튜디오 공식 규칙 확인", status: "todo", dueLabel: "D-28" },
    { id: newId("task"), stage: "onsite", label: "참여자 역할과 촬영 일정 확정", status: "todo", dueLabel: "D-14" },
    { id: newId("task"), stage: "photos", label: "사진 수신·보정·공개 범위 확인", status: "todo", dueLabel: "D+7" },
    { id: newId("task"), stage: "archive", label: "완주 기록과 다음 프로젝트 후보", status: "todo", dueLabel: "D+14" },
  ];
  return {
    id: newId("project"),
    title: `${candidate.character.name} 첫 장면`,
    mediaTitle: candidate.media.title,
    characterName: candidate.character.name,
    characterSourceUrl: candidate.character.siteUrl,
    createdAt: today.toISOString(),
    dDay: "",
    preferences,
    tasks,
    note: `아이디어 찾기에서 시작 · ${candidate.reasons[0] ?? "작품 취향을 확인하고 선택"} · 의상 버전과 예산은 직접 확인`,
  };
}

export function createManualProject(mediaTitle: string, characterName: string, preferences: CosplayPreferences, today = new Date()): CosplayProject {
  const cleanMediaTitle = mediaTitle.trim();
  const cleanCharacterName = characterName.trim();
  if (!cleanMediaTitle || !cleanCharacterName) throw new Error("작품명과 캐릭터명을 모두 입력해 주세요.");
  const tasks: ProjectTask[] = [
    { id: newId("task"), stage: "discover", label: "캐릭터·버전 확정", status: "doing", dueLabel: "오늘" },
    { id: newId("task"), stage: "prepare", label: "의상 파츠와 실측 확인", status: "todo", dueLabel: "D-35" },
    { id: newId("task"), stage: "prepare", label: "행사·스튜디오 공식 규칙 확인", status: "todo", dueLabel: "D-28" },
    { id: newId("task"), stage: "onsite", label: "참여자 역할과 촬영 일정 확정", status: "todo", dueLabel: "D-14" },
    { id: newId("task"), stage: "photos", label: "사진 수신·보정·공개 범위 확인", status: "todo", dueLabel: "D+7" },
    { id: newId("task"), stage: "archive", label: "완주 기록과 다음 프로젝트 후보", status: "todo", dueLabel: "D+14" },
  ];
  return {
    id: newId("project"), title: `${cleanCharacterName} 준비표`, mediaTitle: cleanMediaTitle,
    characterName: cleanCharacterName, characterSourceUrl: "", createdAt: today.toISOString(),
    dDay: "", preferences: { ...preferences, budgetKrw: 0 }, tasks,
    note: "직접 입력으로 시작 · 작품과 의상 버전은 준비하면서 확인",
  };
}

export function createProjectWardrobe(projectId: string): WardrobeItem[] {
  return wardrobeTemplates.map((item) => ({ ...item, status: "unselected", ready: false, dueDate: "", measurementNote: "", id: `${projectId}-${item.id}`, projectId }));
}

export function toggleTask(project: CosplayProject, taskId: string): CosplayProject {
  return {
    ...project,
    tasks: project.tasks.map((task) => {
      if (task.id !== taskId) return task;
      return { ...task, status: task.status === "done" ? "todo" : "done" };
    }),
  };
}

export function projectReadiness(project: CosplayProject): number {
  const done = project.tasks.filter((task) => task.status === "done").length;
  return project.tasks.length ? Math.round((done / project.tasks.length) * 100) : 0;
}

export function activeProject(data: Pick<AppData, "activeProjectId" | "projects">): CosplayProject | undefined {
  return data.projects.find((project) => project.id === data.activeProjectId) ?? data.projects[0];
}

export const defaultBag = () => ["입장권·신분증", "보조배터리", "가발망·실핀", "양면테이프·옷핀", "렌즈·렌즈액", "메이크업 수정 도구", "물·간식", "갈아입을 옷", "큰 비닐봉투(짐 보관)", "소품 고정 끈", "손거울", "상비약"].map(label => ({ id: newId("bag"), label, done: false }));

export function cloneProject(data: AppData, sourceId: string, today = new Date()): AppData {
  const source = data.projects.find(row => row.id === sourceId);
  if (!source) return data;
  const project = { ...createManualProject(source.mediaTitle, source.characterName, source.preferences, today), sourceProjectId: source.id, characterSourceUrl: source.characterSourceUrl };
  const wardrobe = data.wardrobe.filter(row => row.projectId === sourceId).map(row => ({ ...structuredClone(row), id: newId("part"), projectId: project.id, status: "owned" as const, ready: true, reuseEstimate: row.budgetKrw>=0 ? { sourceItemId: row.id, plannedKrw: row.budgetKrw, capturedAt: today.toISOString() } : undefined, budgetKrw: 0, dueDate: "" }));
  return { ...data, activeProjectId: project.id, projects: [project, ...data.projects], wardrobe: [...wardrobe, ...data.wardrobe] };
}

export function removeProject(data: AppData, projectId: string): AppData {
  const rest = <T extends { projectId?: string }>(rows: T[]) => rows.filter(row => row.projectId !== projectId);
  return { ...data, activeProjectId: data.activeProjectId === projectId ? data.projects.find(row => row.id !== projectId)?.id : data.activeProjectId, projects: data.projects.filter(row => row.id !== projectId), wardrobe: rest(data.wardrobe), marketDrafts: rest(data.marketDrafts), shootPlans: rest(data.shootPlans), recruitmentDrafts: rest(data.recruitmentDrafts), bookingDrafts: rest(data.bookingDrafts), photos: rest(data.photos), challengeDrafts: rest(data.challengeDrafts) };
}
